/** Compress an image file to a JPEG data-URI small enough to live in a D1
    row (~100–250 KB at these settings). */
export async function fileToCompressedDataUri(file: File, maxDim = 1100, quality = 0.72): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("Couldn't read that image."));
      i.src = url;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
    const out = canvas.toDataURL("image/jpeg", quality);
    releaseCanvas(canvas);
    img.src = "";
    if (out.length > 800_000) throw new Error("That photo is too large even after compression — try a smaller image.");
    return out;
  } finally {
    URL.revokeObjectURL(url);
  }
}


/* ---------------------------------------------------------- full quality ---
   A site photograph is evidence. At 1100px and quality 0.72 you cannot read a
   crack width, a rebar tag or a batch ticket in it, and once the day has gone
   the photograph cannot be retaken — so the original resolution is worth
   keeping.

   The constraint was never bandwidth. Every photo used to be stored as a base64
   data-URI inside a synced row, which also lands in localStorage: a 5-10 MB
   quota that a few dozen photographs exhaust, at which point new ones are
   silently dropped. Enlarging that row would have made the problem worse, not
   better.

   So the file splits in two, the same way videos and 360 renders already do:
   the FULL-SIZE JPEG goes to R2, which has no row limit and is only fetched
   when somebody actually opens the photograph; the synced row keeps a small
   thumbnail for grids, PDFs and offline use. The net effect is higher quality
   AND a smaller sync payload. */

/** What the row carries: small enough that hundreds fit inside the quota. */
export const THUMB_DIM = 900;
export const THUMB_QUALITY = 0.68;
/** What R2 keeps: the camera's own detail, capped only where browsers stop
    decoding reliably. */
export const FULL_DIM = 4096;
export const FULL_QUALITY = 0.9;
/* A phone cannot hold what a desk can. iOS caps the pixels all live canvases
   may hold at roughly 16.7 million and kills the tab past it — a 4096-wide
   frame of a 12-megapixel photograph next to the decoded original is enough
   to do that, and the crew sees "the screen disappear". So on a touch device
   the kept original is capped at 2560px (still 3× the thumbnail's detail),
   and every canvas is emptied the moment it has been read. */
export function fullDimFor(): number {
  const coarse = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  const lowMem = typeof navigator !== "undefined" && "deviceMemory" in navigator && Number((navigator as Navigator & { deviceMemory?: number }).deviceMemory) <= 4;
  return coarse || lowMem ? 2560 : FULL_DIM;
}
function releaseCanvas(canvas: HTMLCanvasElement) { canvas.width = 0; canvas.height = 0; }

/** Encode one file into the pair the store needs: a row thumbnail and a
    full-quality blob for R2. The blob is optional — an encode that fails still
    yields a usable thumbnail rather than losing the photograph. */
export async function encodePhoto(file: File): Promise<{ thumb: string; full: Blob | null; width: number; height: number }> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("Couldn't read that image."));
      i.src = url;
    });
    const draw = async (dim: number, quality: number, asBlob: boolean) => {
      const scale = Math.min(1, dim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) { releaseCanvas(canvas); throw new Error("The device ran out of memory for this photo — try one at a time."); }
      ctx.drawImage(img, 0, 0, w, h);
      try {
        return asBlob
          ? await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", quality))
          : canvas.toDataURL("image/jpeg", quality);
      } finally { releaseCanvas(canvas); }
    };
    const thumb = await draw(THUMB_DIM, THUMB_QUALITY, false) as string;
    /* Only bother with an R2 original when there is more detail than the
       thumbnail already holds — re-uploading a 800px phone screenshot at 4096
       would cost storage and buy nothing. */
    const worthIt = Math.max(img.width, img.height) > THUMB_DIM * 1.25;
    const full = worthIt ? await draw(fullDimFor(), FULL_QUALITY, true) as Blob | null : null;
    return { thumb, full, width: img.width, height: img.height };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Encode a LOGO. Logos are usually PNGs with a transparent background — the
    photo path above exports JPEG, which has no alpha, so every transparent
    pixel flattens to BLACK (the "black blob" bug). Here we keep PNG so the
    transparency survives and the mark sits cleanly on any background; only if
    the PNG comes out too big for a D1 row do we flatten onto white and fall
    back to JPEG. */
export async function fileToLogoDataUri(file: File, maxDim = 520): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("Couldn't read that image."));
      i.src = url;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(img, 0, 0, w, h);          // transparency preserved
    const png = canvas.toDataURL("image/png");
    if (png.length <= 700_000) return png;
    // Too heavy as PNG — flatten onto white and ship a JPEG instead.
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.9);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Load a File into an <img> and report its natural size + whether it looks
    like a panorama / 360 VR source (≥2:1, or a large square stereo top/bottom). */
export async function isPanoramicFile(file: File): Promise<boolean> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image(); i.onload = () => resolve(i); i.onerror = () => reject(new Error("x")); i.src = url;
    });
    const r = img.width / Math.max(1, img.height);
    return r >= 1.9 || (r >= 0.92 && r <= 1.08 && Math.max(img.width, img.height) >= 2800);
  } catch { return false; } finally { URL.revokeObjectURL(url); }
}

/** Encode a panorama/VR image as large as fits in a synced photo row — steps
    the dimension down until the JPEG is under the aggregate budget, so a huge
    8K stereo render still stores at the best resolution that will sync. */
export async function fileToPanoDataUri(file: File, maxDim = 2560, quality = 0.72): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image(); i.onload = () => resolve(i); i.onerror = () => reject(new Error("unreadable")); i.src = url;
    });
    for (const dim of [maxDim, 2048, 1600, 1280]) {
      const scale = Math.min(1, dim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
      const out = canvas.toDataURL("image/jpeg", quality);
      if (out.length <= 840_000) return out;
    }
    // floor
    return fileToCompressedDataUri(file, 1100, 0.6);
  } finally { URL.revokeObjectURL(url); }
}

/** Encode a File to a high-resolution JPEG Blob for R2 storage (no aggregate
    size cap applies) — used to keep full-quality 360/VR originals that the
    sphere viewer loads, while the synced row keeps only a small thumbnail.
    Capped at 4096 so it stays within WebGL texture limits on all GPUs. */
/* Phones kill the tab (no error, just a reload) when a canvas passes their
   memory budget — iOS refuses past ~16.7M pixels and older Androids sooner.
   Every big canvas is held under this area, smaller on touch devices. */
export function canvasAreaCap(): number {
  try { return matchMedia("(pointer: coarse)").matches ? 8e6 : 16e6; } catch { return 8e6; }
}
export async function fileToJpegBlob(file: File, maxDim = 8192, quality = 0.92): Promise<Blob | null> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image(); i.onload = () => resolve(i); i.onerror = () => reject(new Error("x")); i.src = url;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height), Math.sqrt(canvasAreaCap() / Math.max(1, img.width * img.height)));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    const out = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", quality));
    canvas.width = 0; canvas.height = 0;   // free the bitmap now, not at GC
    return out;
  } catch { return null; } finally { URL.revokeObjectURL(url); }
}

/** Re-encode an existing data-URI down to a small JPEG — used to shrink stored
    site photos before sending them to the vision endpoint so a dozen images
    stay cheap in payload and tokens. Returns the original on any failure. */
export async function shrinkDataUri(dataUri: string, maxDim = 640, quality = 0.6): Promise<string> {
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("unreadable"));
      i.src = dataUri;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", quality);
  } catch {
    return dataUri;
  }
}

/** Same compression, but for an image that lives on another origin (Atelier's
    R2 store). Fetched as bytes so the result is a real data URI — a remote URL
    would render in an <img> but break every PDF export downstream. */
export async function urlToCompressedDataUri(url: string, maxDim = 1400, quality = 0.72): Promise<string> {
  const r = await fetch(url, { mode: "cors", credentials: "omit" });
  if (!r.ok) throw new Error(`Couldn't fetch that image (${r.status}).`);
  const blob = await r.blob();
  if (!/^image\//.test(blob.type)) throw new Error("That link isn't an image.");
  return fileToCompressedDataUri(new File([blob], "image", { type: blob.type }), maxDim, quality);
}

/* --------------------------------------------------------------- video ---
   A site video is stored as a photograph with a moving original: the poster
   still goes in the synced row so galleries, reports and PDFs lay it out like
   any other image, and the file itself goes to R2. Everything below runs in
   the browser off a <video> element — no transcoding, no upload of anything
   we haven't measured first. */

export const VIDEO_MAX_BYTES = 200 * 1024 * 1024;   // 200 MB — a few minutes of phone footage

/** Read a video's first usable frame as a JPEG data-URI, plus its runtime.
    Seeks a little past zero because the very first frame of phone footage is
    often black. Rejects with a readable message when the browser can't decode
    the file, which is the honest answer for an exotic codec. */
export async function videoPoster(file: File, maxDim = 1100, quality = 0.72): Promise<{ poster: string; durationSec: number }> {
  const url = URL.createObjectURL(file);
  try {
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    (v as any).playsInline = true;
    v.src = url;

    await new Promise<void>((resolve, reject) => {
      v.onloadedmetadata = () => resolve();
      v.onerror = () => reject(new Error("That video couldn't be read — try an MP4 or MOV."));
      setTimeout(() => reject(new Error("That video took too long to open.")), 20_000);
    });

    const durationSec = isFinite(v.duration) ? Math.round(v.duration) : 0;
    // a fraction in, so a black opening frame doesn't become the poster
    const at = Math.min(durationSec > 2 ? 1 : 0.1, Math.max(0, (v.duration || 1) - 0.05));
    await new Promise<void>((resolve, reject) => {
      v.onseeked = () => resolve();
      v.onerror = () => reject(new Error("That video couldn't be read."));
      setTimeout(() => resolve(), 8_000);   // some codecs never fire onseeked
      v.currentTime = at;
    });

    const scale = Math.min(1, maxDim / Math.max(v.videoWidth || maxDim, v.videoHeight || maxDim));
    const w = Math.max(1, Math.round((v.videoWidth || maxDim) * scale));
    const h = Math.max(1, Math.round((v.videoHeight || maxDim) * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d")!.drawImage(v, 0, 0, w, h);
    const poster = canvas.toDataURL("image/jpeg", quality);
    if (!poster.startsWith("data:image/jpeg")) throw new Error("That video's frames couldn't be captured.");
    return { poster, durationSec };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** "1:04" / "12:07" — a runtime the way a person reads one. */
export function runtime(sec: number | undefined): string {
  if (!sec || sec < 1) return "";
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
